import { useState } from "react";
import { Pencil, Loader2, MessageSquareText, X } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { EtapaIcon } from "@/components/rastreo/EtapaIcon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";

const NOMBRES_VARIABLES = {
  marca: "Marca", modelo: "Modelo", vin_last6: "VIN (6 últimos)",
  etapa: "Etapa", fecha: "Fecha", link: "Link de rastreo",
};

export default function Plantillas() {
  usePageTitle("Plantillas de notificación");
  const queryClient = useQueryClient();
  const [dialogo, setDialogo] = useState(null); // { etapa, plantilla }

  const { data: items, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-plantillas"],
    queryFn: () => adminApi.get("/admin/plantillas").then((r) => r.data),
  });

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Plantillas de notificación</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
        Un mensaje por etapa. Se usa tal cual para Email y para el modo Mock. Para WhatsApp,
        el texto es solo referencia: lo que realmente se envía es la plantilla aprobada por
        Meta (nombre + idioma + orden de variables) hasta que la editen y la vuelvan a aprobar.
      </p>

      <div className="mt-6 overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} /></div>
        ) : isError ? (
          <ErrorState testId="admin-plantillas-error" mensaje="No se pudieron cargar las plantillas." onRetry={refetch} />
        ) : (
          <div className="divide-y divide-[var(--tc-border)]">
            {items.map(({ etapa, plantilla }) => (
              <div key={etapa.id} data-testid={ADMIN.plantillasRow} className="flex items-start justify-between gap-4 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                    <EtapaIcon nombre={etapa.icono} size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[var(--tc-text)]">{etapa.nombre}</p>
                    <p className="mt-1 max-w-md text-xs leading-relaxed text-[var(--tc-text-secondary)]">{plantilla?.texto}</p>
                    <p className="mt-1.5 text-[11px] text-[var(--tc-text-secondary)]">
                      WhatsApp: {plantilla?.whatsapp_template_name ? (
                        <span className="font-medium text-[var(--tc-accent)]">{plantilla.whatsapp_template_name} ({plantilla.whatsapp_idioma})</span>
                      ) : (
                        <span className="italic">sin plantilla de Meta configurada</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={ADMIN.plantillasEditButton}
                  onClick={() => setDialogo({ etapa, plantilla })}
                  className="shrink-0 rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]"
                >
                  <Pencil size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {dialogo && (
        <PlantillaDialog
          dialogo={dialogo}
          onClose={() => setDialogo(null)}
          onSaved={() => { setDialogo(null); queryClient.invalidateQueries({ queryKey: ["admin-plantillas"] }); }}
        />
      )}
    </div>
  );
}

function PlantillaDialog({ dialogo, onClose, onSaved }) {
  const { etapa, plantilla } = dialogo;
  const [texto, setTexto] = useState(plantilla?.texto || "");
  const [nombrePlantilla, setNombrePlantilla] = useState(plantilla?.whatsapp_template_name || "");
  const [idioma, setIdioma] = useState(plantilla?.whatsapp_idioma || "es");
  const [parametros, setParametros] = useState(plantilla?.whatsapp_parametros || []);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const agregarParametro = (v) => setParametros((p) => (p.includes(v) ? p : [...p, v]));
  const quitarParametro = (i) => setParametros((p) => p.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await adminApi.put(`/admin/plantillas/${etapa.id}`, {
        texto, whatsapp_template_name: nombrePlantilla || null,
        whatsapp_idioma: idioma, whatsapp_parametros: parametros,
      });
      toast.success("Plantilla guardada");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Plantilla: {etapa.nombre}</DialogTitle>
          <DialogDescription>Mensaje que se envía al destinatario cuando la unidad entra a esta etapa.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto pr-1">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Texto (Email y Mock)</label>
            <textarea
              data-testid={ADMIN.plantillaFormTexto}
              required rows={4}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(NOMBRES_VARIABLES).map(([clave, etiqueta]) => (
                <button
                  type="button"
                  key={clave}
                  data-testid={ADMIN.plantillaFormVariableChip}
                  onClick={() => setTexto((t) => `${t}{${clave}}`)}
                  className="rounded-full bg-[var(--tc-accent-soft)] px-2.5 py-1 text-[11px] font-medium text-[var(--tc-accent)] hover:bg-[var(--tc-accent)] hover:text-white"
                >
                  + {etiqueta}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-[var(--tc-surface)] p-4">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--tc-text-secondary)]">
              <MessageSquareText size={13} /> WhatsApp (Meta)
            </p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-[var(--tc-text-secondary)]">
              Editar esto no cambia lo que se envía por WhatsApp hasta que la plantilla esté
              aprobada por Meta con este mismo nombre e idioma.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[var(--tc-text)]">Nombre de la plantilla en Meta</label>
                <input
                  data-testid={ADMIN.plantillaFormWhatsappNombre}
                  value={nombrePlantilla}
                  onChange={(e) => setNombrePlantilla(e.target.value)}
                  placeholder="ej: unidad_actualizacion"
                  className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[var(--tc-text)]">Idioma</label>
                <select
                  data-testid={ADMIN.plantillaFormWhatsappIdioma}
                  value={idioma}
                  onChange={(e) => setIdioma(e.target.value)}
                  className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
                >
                  <option value="es">es (genérico)</option>
                  <option value="es_MX">es_MX (México)</option>
                  <option value="es_CO">es_CO (Colombia)</option>
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className="mb-1.5 block text-xs font-medium text-[var(--tc-text)]">
                Orden de variables → parámetros {"{{1}}"}, {"{{2}}"}... de la plantilla
              </label>
              <div className="flex min-h-[34px] flex-wrap gap-1.5 rounded-[var(--tc-radius)] border border-dashed border-[var(--tc-border)] bg-white p-2">
                {parametros.length === 0 && <span className="text-xs text-[var(--tc-state-pending)]">Sin variables asignadas</span>}
                {parametros.map((p, i) => (
                  <span
                    key={`${p}-${i}`}
                    data-testid={ADMIN.plantillaFormWhatsappParamChip}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--tc-accent)] px-2.5 py-1 text-[11px] font-semibold text-white"
                  >
                    {"{{"}{i + 1}{"}}"} {NOMBRES_VARIABLES[p]}
                    <button type="button" data-testid={ADMIN.plantillaFormWhatsappParamRemove} onClick={() => quitarParametro(i)}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(NOMBRES_VARIABLES).map(([clave, etiqueta]) => (
                  <button
                    type="button"
                    key={clave}
                    data-testid={ADMIN.plantillaFormWhatsappParamAdd}
                    onClick={() => agregarParametro(clave)}
                    className="rounded-full border border-[var(--tc-border)] bg-white px-2.5 py-1 text-[11px] font-medium text-[var(--tc-text-secondary)] hover:border-[var(--tc-accent)] hover:text-[var(--tc-accent)]"
                  >
                    + {etiqueta}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && <p className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.plantillaFormSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
